<template>
  <div>
    <SelfServiceForm
      v-if="!loading"
      v-model="changeAddressRequest.startDate"
      :is-loading="loading"
      :isSubmitButtonDisabled="submitDisabled"
      :isSubmitting="isSubmitting"
      :self-service-labels="selfServiceLabels"
      :show-response="showResponse"
      @button:submit="submit"
      @button:abort="abort"
      @button:back="abort"
    >
      <template v-slot:main>
        <AddressInput
          @input="updateAddressInput"
          class="address-input"
          :addressData="newAddressData"
          :cityInfo="t('selfServices.changeAddress.enterNewCity')"
          :postCodeInfo="t('selfServices.changeAddress.enterNewPostCode')"
          :postalServiceUrl="postalServiceURL"
          :postalServiceUrlApiKey="postalServiceApiKey"
          :selectMenueHint="t('selfServices.changeAddress.enterNewStreet')"
          :streetExtraInfo="t('selfServices.changeAddress.enterNewStreetExtra')"
          :streetInfo="t('selfServices.changeAddress.enterNewStreet')"
          :streetNumberInfo="t('selfServices.changeAddress.enterNewStreetNumber')"
          @input:status="updateAddressInputStatus"
        ></AddressInput>
      </template>
    </SelfServiceForm>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, reactive, ref } from 'vue'
import {
  type AddressData,
  AddressInput,
  InputStatus,
  SelfServiceForm
} from 'de-zurich-ui-library'
import { getPartnerProxyId } from '@/utils/KeycloakUtils'
import { navigate } from '@/utils/NavigationUtils'
import type { ChangeAddressRequest, Partner, PhysicalAddress } from 'partner-api'
import { ResponseError } from 'partner-api'
import { partnerApi } from '@/stores/partnerApiClient'

import { useI18n } from 'vue-i18n'
import { fullNameWithSalutation } from '@/utils/PartnerUtils'

const { t } = useI18n()

const partner = ref<Partner | null>(null)
const partnerProxyId = ref('')
const newAddressData = ref({
  city: '',
  postCode: '',
  street: '',
  streetExtra: '',
  streetNumber: ''
})
const addressInputStatus = ref<InputStatus>(InputStatus.DEFAULT)
const showResponse = ref(false)
const loading = ref(true)
const isSubmitting = ref(false)

const changeAddressRequest = reactive<ChangeAddressRequest>({
  id: '',
  newCity: '',
  newPostCode: '',
  newStreet: '',
  newStreetNumber: '',
  newStreetExtra: '',
  startDate: ''
})

const selfServiceLabels = reactive({
  listHeadline: '',
  formCardTitle: '',
  formCurrentDataLabel: '',
  formCurrentDataValue: '',
  formCurrentInfoText: '',
  formDateLabel: '',
  responseText: '',
  responseTitle: '',
  responseTileBackButtonLabel: '',
  formDateErrorText: '',
  errorTitle: '',
  errorText: ''
})

const postalServiceURL = globalThis.ENV_CONFIG?.SERVICE_HOSTS.POSTAL
const postalServiceApiKey = globalThis.ENV_CONFIG?.API_KEY

// PostalAddress-Basistyp im generierten Client hat keinen expliziten
// Diskriminator-Feld, daher Duck-Typing anhand des 'street'-Feldes.
const isPhysicalAddress = (address: object): address is PhysicalAddress => {
  return 'street' in address
}

const prepareLabels = (): void => {
  // build change request model

  // build data for layout
  selfServiceLabels.formCurrentDataLabel = t(
    'selfServices.changeAddress.currentAddress'
  ).toString()
  selfServiceLabels.formCurrentDataValue = ''
  selfServiceLabels.formCurrentInfoText = `${fullNameWithSalutation(partner.value as Partner)} &middot; ${fullAddress()}`
  selfServiceLabels.formCardTitle = t('selfServices.changeAddress.cardTitle').toString()
  selfServiceLabels.formDateLabel = t(
    'selfServices.changeAddress.whenIsNewAddressValid'
  ).toString()
  selfServiceLabels.formDateErrorText = t('selfServices.dateErrorText').toString()
  selfServiceLabels.responseText = t(
    'selfServices.response.thankYouTextAddress'
  ).toString()
  selfServiceLabels.responseTitle = t('selfServices.response.thankYouTitle').toString()
  selfServiceLabels.responseTileBackButtonLabel = t(
    'selfServices.button.linkToDetails'
  ).toString()
  selfServiceLabels.errorTitle = ''
  selfServiceLabels.errorText = ''
}

const fullAddress = (): string => {
  if (!partner.value) return ''
  const partnerAddresses = partner.value.partnerAddresses

  // search physical address
  const physicalAddress = partnerAddresses?.find(
    (partnerAddress) =>
      partnerAddress.address && isPhysicalAddress(partnerAddress.address)
  )?.address as PhysicalAddress | undefined

  if (!physicalAddress) return ''

  return `${physicalAddress.street} &middot; ${physicalAddress.postCode} ${physicalAddress.city}`
}

const submitDisabled = computed(() => {
  return addressInputStatus.value !== InputStatus.VALID
})

onMounted(async () => {
  const partnerProxyIdVal = getPartnerProxyId()
  if (!partnerProxyIdVal) {
    throw new Error('No partner proxy id available!')
  }
  loading.value = true
  partnerProxyId.value = partnerProxyIdVal

  try {
    const response = await partnerApi().getPartner({
      partnerProxyId: partnerProxyId.value
    })
    partner.value = response.data ?? null
    prepareLabels()
  } catch (err) {
    setErrorResponse()
    if (!(err instanceof ResponseError)) {
      console.error('Unknown error:', err)
    }
  } finally {
    loading.value = false
  }
})

const setErrorResponse = () => {
  selfServiceLabels.responseText = t('selfServices.response.serviceErrorText').toString()
  selfServiceLabels.responseTitle = t(
    'selfServices.response.serviceErrorTitle'
  ).toString()
  selfServiceLabels.responseTileBackButtonLabel = t(
    'selfServices.button.linkToDetails'
  ).toString()
  showResponse.value = true
}

const submit = async () => {
  isSubmitting.value = true
  changeAddressRequest.id = partnerProxyId.value
  changeAddressRequest.newPostCode = newAddressData.value.postCode
  changeAddressRequest.newCity = newAddressData.value.city
  changeAddressRequest.newStreet = newAddressData.value.street
  changeAddressRequest.newStreetNumber = newAddressData.value.streetNumber
  changeAddressRequest.newStreetExtra = newAddressData.value.streetExtra

  try {
    await partnerApi().changeAddress({ changeAddressRequest })
    navigate('/danke?adresse-aendern')
  } catch (err) {
    setErrorResponse()
    if (!(err instanceof ResponseError)) {
      throw new Error('Unknown error:' + err)
    }
  } finally {
    isSubmitting.value = false
  }
}

const abort = (): void => {
  navigate(globalThis.ENV_CONFIG?.REDIRECTS.PERSONAL_PROFIL_PAGE)
}

const updateAddressInputStatus = (inputStatus: InputStatus) => {
  addressInputStatus.value = inputStatus
}

const updateAddressInput = (input: AddressData) => {
  newAddressData.value = input
}
</script>

<style scoped>
@media only screen and (min-width: 427px) {
  .address-input {
    width: 75%;
  }
}
</style>
